import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { setAuthSession } from '../utils/auth';
import { useB2BAuthStore } from '../stores/useB2BAuthStore';
import { pushDataLayer } from '../utils/dataLayer';
import { trackEvent } from '../utils/amplitude';

async function exchangeGoogleCode(code, endpoint) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      redirect_uri: `${window.location.origin}/auth/google/callback`,
    }),
  });

  const data = await response.json().catch(() => ({}));
  return { response, data };
}

export default function GoogleCallbackScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const authModal = useAuthModal();
  const b2bLogin = useB2BAuthStore((state) => state.login);

  const handleGoogleCallback = useCallback(async () => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const isEmployer = searchParams.get('state') === 'employer';
    const loginRedirect = isEmployer ? '/b2b/auth' : '/login';

    if (errorParam) {
      setError('Access to the Google account was denied');
      setLoading(false);
      setTimeout(() => navigate(loginRedirect), 3000);
      return;
    }

    if (!code) {
      setError('Authorization code was not received');
      setLoading(false);
      setTimeout(() => navigate(loginRedirect), 3000);
      return;
    }

    try {
      const endpoint = isEmployer ? '/api/b2b/auth/google' : '/api/auth/google';
      const { response, data } = await exchangeGoogleCode(code, endpoint);

      if (!response.ok) {
        setError(data.detail || data.message || 'Google authorization failed');
        setLoading(false);
        setTimeout(() => navigate(loginRedirect), 3000);
        return;
      }

      if (isEmployer) {
        b2bLogin({ token: data.token, member: data.member, workspace: data.workspace });

        pushDataLayer({
          event: 'auth_success',
          type: data.is_new_user ? 'sign_up' : 'login',
          method: 'google',
          account_type: 'employer',
          user_id: data.member?.id ? String(data.member.id) : null,
          data_source: 'back',
        });
        trackEvent(data.is_new_user ? 'b2b_first_login' : 'b2b_login');

        navigate('/b2b/dashboard');
        return;
      }

      if (data.token || data.user?.id) {
        setAuthSession(data.token, data.user?.id);
      }

      pushDataLayer({
        event: 'auth_success',
        type: data.is_new_user ? 'sign_up' : 'login',
        method: 'google',
        account_type: 'candidate',
        user_id: data.user?.id ? String(data.user.id) : null,
        data_source: 'back',
      });
      trackEvent(data.is_new_user ? 'first_login' : 'login');

      navigate('/search');
    } catch (err) {
      console.error('Error during Google auth:', err);
      setError('An error occurred during Google authorization');
      setLoading(false);
      setTimeout(() => navigate(loginRedirect), 3000);
    }
  }, [navigate, searchParams, b2bLogin]);

  useEffect(() => {
    handleGoogleCallback();
  }, [handleGoogleCallback]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader authModal={authModal} />
      <div className="flex flex-col items-center justify-center p-6">
        <div className="text-center">
          {loading ? (
            <>
              <div className="mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800">
                Signing in with Google...
              </h2>
              <p className="text-gray-600 mt-2">
                Please wait
              </p>
            </>
          ) : error ? (
            <>
              <div className="mb-4">
                <svg
                  className="h-12 w-12 text-red-500 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800">
                Authorization error
              </h2>
              <p className="text-red-600 mt-2">{error}</p>
              <p className="text-gray-600 mt-4 text-sm">
                Redirecting to the login page...
              </p>
            </>
          ) : null}
        </div>
      </div>
      <Footer />
    </div>
  );
}
